import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, FileText, Calendar, User, LogOut, GraduationCap, Archive, Trash2, ArchiveRestore, MessageSquare, Share2, Settings, Building2, CheckCircle2, Copy, Filter, ChevronDown, ChevronUp, BookOpen, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const Dashboard = ({ userData, onLogout }) => {
  const [screenings, setScreenings] = useState([]);
  const [adminOrgs, setAdminOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState(null);
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);
  const navigate = useNavigate();

  const isSLP = userData?.user_type === 'slp';
  const isSuperadmin = userData?.is_superadmin;
  const isTeacher = userData?.user_type === 'teacher';

  const fetchScreenings = useCallback(async () => {
    setLoading(true);
    try {
      if (isSuperadmin) {
        const response = await axios.get(`${API}/admin/organizations`, { withCredentials: true });
        setAdminOrgs(response.data);
      } else {
        const includeArchived = statusFilter === 'archived' || statusFilter === 'all';
        const params = { include_archived: includeArchived };
        if (teacherFilter) params.teacher_filter = teacherFilter;
        const response = await axios.get(`${API}/screenings`, { params, withCredentials: true });
        setScreenings(response.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isSuperadmin, teacherFilter]);

  useEffect(() => { fetchScreenings(); }, [fetchScreenings]);

  const handleArchive = async (screeningId, currentlyArchived, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/screening/${screeningId}/archive`, null, { params: { archived: !currentlyArchived }, withCredentials: true });
      toast.success(currentlyArchived ? 'Referral restored' : 'Referral archived');
      fetchScreenings();
    } catch (error) { toast.error('Failed to archive'); }
  };

  const handleReview = async (screeningId, currentlyReviewed, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/screening/${screeningId}/review`, null, { params: { reviewed: !currentlyReviewed }, withCredentials: true });
      toast.success(!currentlyReviewed ? 'Marked as reviewed' : 'Marked as new');
      fetchScreenings();
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleDelete = async (screeningId, studentName, e) => {
    e.stopPropagation();
    if (window.confirm(`Permanently delete the referral for ${studentName}?`)) {
      try {
        await axios.delete(`${API}/screening/${screeningId}`, { withCredentials: true });
        toast.success('Referral deleted');
        fetchScreenings();
      } catch (error) { toast.error('Failed to delete'); }
    }
  };

  const handleLogout = () => { onLogout(); navigate('/'); };

  const handleShare = async () => {
    const shareUrl = window.location.origin;
    const shareText = 'Check out FrameEd Referrals — a referral platform for K-8 schools!';
    if (navigator.share) {
      try { await navigator.share({ title: 'FrameEd', text: shareText, url: shareUrl }); } catch (e) {}
    } else {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Link copied!');
    }
  };

  const copyJoinCode = async () => {
    if (userData?.join_code) {
      await navigator.clipboard.writeText(userData.join_code);
      toast.success('School code copied! Share it with your teachers.');
    }
  };

  const viewOrgUsers = async (org) => {
    setSelectedOrg(org);
    setOrgUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/organizations/${org.id}/users`, { withCredentials: true });
      setOrgUsers(response.data);
    } catch (error) {
      toast.error('Failed to load organization details');
    } finally {
      setOrgUsersLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const uniqueTeachers = isSLP ? [...new Set(screenings.map(s => s.teacher_email).filter(Boolean))] : [];

  const filteredScreenings = screenings.filter(s => {
    if (statusFilter === 'active') return !s.archived && !s.reviewed;
    if (statusFilter === 'reviewed') return !s.archived && s.reviewed;
    if (statusFilter === 'archived') return true;
    return true;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fe-bg)' }}>
      {/* FrameEd Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#E5E5E5] h-16">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#2C4C3B]" />
              <span className="text-xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>FrameEd</span>
            </div>
            <span className="text-[#E5E5E5]">|</span>
            <div>
              <p className="text-sm font-medium text-[#1C1C1C]" data-testid="dashboard-title">Referral Dashboard</p>
              <p className="text-xs text-[#595959]">
                {userData?.organization_name || 'My School'}
                {isSuperadmin && <span className="ml-2 px-2 py-0.5 bg-[#2C4C3B]/10 text-[#2C4C3B] rounded-full text-[10px] font-semibold uppercase tracking-wide">Superadmin</span>}
                {isTeacher && <span className="ml-1 text-[#A3A3A3]">({userData?.teacher_email})</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="share-button" onClick={handleShare} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E5E5E5] text-[#595959] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 text-sm transition-all">
              <Share2 className="w-4 h-4" /><span className="hidden sm:inline">Share</span>
            </button>
            <button data-testid="feedback-button" onClick={() => navigate('/feedback')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E5E5E5] text-[#595959] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 text-sm transition-all">
              <MessageSquare className="w-4 h-4" /><span className="hidden sm:inline">Feedback</span>
            </button>
            {isSLP && !isSuperadmin && (
              <button data-testid="settings-button" onClick={() => navigate('/settings')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E5E5E5] text-[#595959] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 text-sm transition-all">
                <Settings className="w-4 h-4" /><span className="hidden sm:inline">Settings</span>
              </button>
            )}
            <button data-testid="logout-button" onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[#595959] hover:text-[#1C1C1C] hover:bg-[#F3F4F1] text-sm transition-all">
              <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {isSuperadmin ? (
          <>
            {selectedOrg && orgUsers ? (
              /* ---- Org Detail View ---- */
              <>
                <button onClick={() => { setSelectedOrg(null); setOrgUsers(null); }} className="inline-flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] mb-6 transition-colors text-sm">
                  <ArrowLeft className="w-4 h-4" />Back to All Organizations
                </button>
                <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-xl flex items-center justify-center"><Building2 className="w-6 h-6 text-[#2C4C3B]" /></div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>{orgUsers.organization.name}</h2>
                      <p className="text-sm text-[#595959]">
                        {orgUsers.organization.district && `${orgUsers.organization.district} · `}
                        Code: <span className="font-mono font-semibold text-[#2C4C3B]">{orgUsers.organization.join_code || '—'}</span>
                        {' · '}Joined {formatDate(orgUsers.organization.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center"><p className="text-2xl font-semibold text-[#1C1C1C]">{selectedOrg.screening_count}</p><p className="text-[10px] text-[#595959] uppercase tracking-wider">Referrals</p></div>
                      <div className="text-center"><p className="text-2xl font-semibold text-[#1C1C1C]">{selectedOrg.slp_count}</p><p className="text-[10px] text-[#595959] uppercase tracking-wider">SLPs</p></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
                  <div className="p-6 border-b border-[#E5E5E5]">
                    <h3 className="text-lg font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>SLPs at this School</h3>
                    <p className="text-sm text-[#595959] mt-1">{orgUsers.users.length} user{orgUsers.users.length !== 1 ? 's' : ''}</p>
                  </div>
                  {orgUsersLoading ? (
                    <div className="p-12 text-center"><div className="inline-block w-8 h-8 border-4 border-[#2C4C3B] border-t-transparent rounded-full animate-spin" /></div>
                  ) : orgUsers.users.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-[#F3F4F1] flex items-center justify-center mx-auto mb-4"><User className="w-6 h-6 text-[#A3A3A3]" /></div>
                      <p className="text-[#595959]">No SLPs registered at this school yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#E5E5E5]">
                      {orgUsers.users.map((user) => (
                        <div key={user.id} data-testid={`user-row-${user.id}`} className="p-5 flex items-center gap-4">
                          <div className="w-9 h-9 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center"><User className="w-4 h-4 text-[#2C4C3B]" /></div>
                          <div className="flex-1">
                            <p className="text-base font-medium text-[#1C1C1C]">{user.name}</p>
                            <p className="text-sm text-[#595959] flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{user.email}</p>
                          </div>
                          <div className="text-right">
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#2C4C3B]/10 text-[#2C4C3B]">{user.role}</span>
                            {user.created_at && <p className="text-xs text-[#A3A3A3] mt-1">Joined {formatDate(user.created_at)}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* ---- Org List View ---- */
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 card-hover">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#2C4C3B]" /></div>
                      <span className="text-3xl font-semibold text-[#1C1C1C]">{adminOrgs.length}</span>
                    </div>
                    <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider">Organizations</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 card-hover">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#3B6A9B]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#3B6A9B]" /></div>
                      <span className="text-3xl font-semibold text-[#1C1C1C]">{adminOrgs.reduce((s, o) => s + o.screening_count, 0)}</span>
                    </div>
                    <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider">Total Referrals</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 card-hover">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#5A8A5A]/10 flex items-center justify-center"><User className="w-5 h-5 text-[#5A8A5A]" /></div>
                      <span className="text-3xl font-semibold text-[#1C1C1C]">{adminOrgs.reduce((s, o) => s + o.slp_count, 0)}</span>
                    </div>
                    <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider">Total SLPs</p>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
                  <div className="p-6 border-b border-[#E5E5E5]">
                    <h2 className="text-xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Organizations Using FrameEd</h2>
                    <p className="text-sm text-[#595959] mt-1">Click an organization to view its users</p>
                  </div>
                  {loading ? (
                    <div className="p-12 text-center"><div className="inline-block w-8 h-8 border-4 border-[#2C4C3B] border-t-transparent rounded-full animate-spin" /><p className="mt-4 text-[#595959]">Loading...</p></div>
                  ) : (
                    <div className="divide-y divide-[#E5E5E5]">
                      {adminOrgs.map((org) => (
                        <div key={org.id} data-testid={`org-row-${org.id}`} onClick={() => viewOrgUsers(org)} className="p-6 hover:bg-[#F3F4F1] transition-colors cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-[#2C4C3B]/10 rounded-xl flex items-center justify-center"><Building2 className="w-5 h-5 text-[#2C4C3B]" /></div>
                              <div>
                                <h3 className="text-base font-semibold text-[#1C1C1C]">{org.name}</h3>
                                {org.district && <p className="text-sm text-[#595959]">{org.district}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-6 text-sm">
                              <div className="text-center"><p className="text-xl font-semibold text-[#1C1C1C]">{org.screening_count}</p><p className="text-[10px] text-[#595959] uppercase tracking-wider">Referrals</p></div>
                              <div className="text-center"><p className="text-xl font-semibold text-[#1C1C1C]">{org.slp_count}</p><p className="text-[10px] text-[#595959] uppercase tracking-wider">SLPs</p></div>
                              <div className="text-right"><p className="text-xs text-[#A3A3A3]">{org.last_activity ? `Last: ${formatDate(org.last_activity)}` : 'No activity'}</p><p className="text-xs text-[#A3A3A3]">Joined: {formatDate(org.created_at)}</p></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 card-hover">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#2C4C3B]" /></div>
                  <span className="text-3xl font-semibold text-[#1C1C1C]">{screenings.length}</span>
                </div>
                <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider">{isTeacher ? 'My Referrals' : 'Total Referrals'}</p>
              </div>

              {isSLP && userData?.join_code && (
                <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 card-hover">
                  <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider mb-2">School Code</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-mono font-semibold text-[#2C4C3B] tracking-widest" data-testid="join-code-display">{userData.join_code}</span>
                    <button onClick={copyJoinCode} data-testid="copy-join-code" className="p-1.5 rounded-xl hover:bg-[#F3F4F1] transition-colors" title="Copy code"><Copy className="w-4 h-4 text-[#595959]" /></button>
                  </div>
                  <p className="text-xs text-[#A3A3A3] mt-1">Share with your teachers</p>
                </div>
              )}

              <div className={`${isSLP && userData?.join_code ? 'md:col-span-2' : 'md:col-span-3'} bg-[#2C4C3B] rounded-2xl p-6 text-white card-hover`}>
                <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{isTeacher ? 'Refer a student to your SLP' : 'Ready to start a new referral?'}</h3>
                <p className="text-white/80 mb-4 text-sm">Begin a comprehensive speech & language assessment</p>
                <button data-testid="start-new-screening-button" onClick={() => navigate('/screening')} className="bg-white text-[#2C4C3B] font-medium rounded-xl px-5 py-2.5 hover:shadow-lg transition-all flex items-center gap-2 text-sm">
                  <Plus className="w-4 h-4" />Start New Referral
                </button>
              </div>
            </div>

            {/* Filters (SLP only) */}
            {isSLP && (
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-1 bg-white rounded-xl border border-[#E5E5E5] p-1">
                  {[
                    { value: 'active', label: 'New' },
                    { value: 'reviewed', label: 'Reviewed' },
                    { value: 'archived', label: 'Archived' },
                    { value: 'all', label: 'All' }
                  ].map((tab) => (
                    <button key={tab.value} data-testid={`filter-status-${tab.value}`} onClick={() => setStatusFilter(tab.value)}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${statusFilter === tab.value ? 'bg-[#2C4C3B] text-white' : 'text-[#595959] hover:bg-[#F3F4F1]'}`}>
                      {tab.label}
                      <span className="ml-1 text-xs opacity-75">({screenings.filter(s => {
                        if (tab.value === 'active') return !s.archived && !s.reviewed;
                        if (tab.value === 'reviewed') return !s.archived && s.reviewed;
                        if (tab.value === 'archived') return s.archived;
                        return true;
                      }).length})</span>
                    </button>
                  ))}
                </div>
                {uniqueTeachers.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#595959]" />
                    <select data-testid="teacher-filter-select" value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)}
                      className="px-3 py-2 rounded-xl border border-[#E5E5E5] text-sm bg-white text-[#1C1C1C] h-9">
                      <option value="">All Teachers</option>
                      {uniqueTeachers.map((email) => (<option key={email} value={email}>{email}</option>))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Referrals List */}
            <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
              <div className="p-6 border-b border-[#E5E5E5]">
                <h2 className="text-xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>{isTeacher ? 'My Referrals' : 'Recent Referrals'}</h2>
                <p className="text-sm text-[#595959] mt-1">{isTeacher ? 'Referrals you have submitted' : 'View and manage student assessments'}</p>
              </div>
              {loading ? (
                <div className="p-12 text-center"><div className="inline-block w-8 h-8 border-4 border-[#2C4C3B] border-t-transparent rounded-full animate-spin" /><p className="mt-4 text-[#595959]">Loading referrals...</p></div>
              ) : filteredScreenings.length === 0 ? (
                <div className="p-16 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-[#F3F4F1] flex items-center justify-center mx-auto mb-4"><FileText className="w-6 h-6 text-[#A3A3A3]" /></div>
                  <p className="text-base font-medium text-[#1C1C1C] mb-1">No referrals yet</p>
                  <p className="text-sm text-[#595959]">{isTeacher ? 'Start your first referral above' : 'No referrals match this filter'}</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E5E5E5]">
                  {filteredScreenings.map((screening) => (
                    <div key={screening.id} data-testid={`screening-item-${screening.id}`} className="p-5 hover:bg-[#F3F4F1] transition-colors group">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => navigate(`/results/${screening.id}`)}>
                          <div className="flex items-center gap-3 mb-1.5">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${screening.archived ? 'bg-[#F3F4F1]' : screening.reviewed ? 'bg-[#3B6A9B]/10' : 'bg-[#2C4C3B]/10'}`}>
                              <User className={`w-4 h-4 ${screening.archived ? 'text-[#A3A3A3]' : screening.reviewed ? 'text-[#3B6A9B]' : 'text-[#2C4C3B]'}`} />
                            </div>
                            <div>
                              <h3 className={`text-base font-semibold group-hover:text-[#2C4C3B] transition-colors ${screening.archived ? 'text-[#A3A3A3]' : 'text-[#1C1C1C]'}`}>
                                {screening.student_name}
                                {screening.archived && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-[#F3F4F1] text-[#595959]">Archived</span>}
                                {screening.reviewed && !screening.archived && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">Reviewed</span>}
                              </h3>
                              <p className="text-sm text-[#595959]">
                                Concern: <span className="capitalize font-medium">{Array.isArray(screening.concern_type) ? screening.concern_type.join(', ') : screening.concern_type}</span>
                                {isSLP && screening.teacher_email && <span className="ml-2 text-[#A3A3A3]">by {screening.teacher_email}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="ml-12 flex items-center gap-3 text-sm text-[#595959]">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(screening.created_at)}</span>
                            <span className="px-2 py-0.5 bg-[#F3F4F1] text-[#595959] rounded-full text-xs">{screening.recommendations?.length || 0} recommendations</span>
                          </div>
                        </div>
                        {isSLP && (
                          <div className="flex items-center gap-1 ml-3">
                            {!screening.archived && (
                              <button onClick={(e) => handleReview(screening.id, screening.reviewed, e)} data-testid={`review-button-${screening.id}`} className="p-2 rounded-xl hover:bg-[#3B6A9B]/10 transition-colors" title={screening.reviewed ? 'Mark as new' : 'Mark as reviewed'}>
                                <CheckCircle2 className={`w-4 h-4 ${screening.reviewed ? 'text-[#3B6A9B]' : 'text-[#A3A3A3]'}`} />
                              </button>
                            )}
                            <button onClick={(e) => handleArchive(screening.id, screening.archived, e)} data-testid={`archive-button-${screening.id}`} className="p-2 rounded-xl hover:bg-[#F3F4F1] transition-colors" title={screening.archived ? 'Restore' : 'Archive'}>
                              {screening.archived ? <ArchiveRestore className="w-4 h-4 text-[#595959]" /> : <Archive className="w-4 h-4 text-[#595959]" />}
                            </button>
                            <button onClick={(e) => handleDelete(screening.id, screening.student_name, e)} data-testid={`delete-button-${screening.id}`} className="p-2 rounded-xl hover:bg-red-50 transition-colors" title="Delete">
                              <Trash2 className="w-4 h-4 text-[#595959]" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isTeacher && <ReferralProcessGuide />}
          </>
        )}
      </main>
    </div>
  );
};

const ReferralProcessGuide = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-8 bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden" data-testid="referral-process-guide">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-6 flex items-center justify-between text-left hover:bg-[#F3F4F1] transition-colors" data-testid="referral-guide-toggle">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E2DACD]/50 rounded-xl flex items-center justify-center"><BookOpen className="w-5 h-5 text-[#6B5D4D]" /></div>
          <div>
            <h2 className="text-lg font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Understanding the Referral Process</h2>
            <p className="text-sm text-[#595959]">Learn how your referral helps students get the support they need</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-[#A3A3A3]" /> : <ChevronDown className="w-5 h-5 text-[#A3A3A3]" />}
      </button>
      {expanded && (
        <div className="px-6 pb-8 space-y-6 border-t border-[#E5E5E5] pt-6">
          <div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>The Two Requirements for Eligibility under Speech/Language Impairment (SLI)</h3>
            <p className="text-sm text-[#595959] mb-4">For a student to qualify for special education services under a speech/language impairment, two things must both be true:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#2C4C3B]/5 border border-[#2C4C3B]/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-5 h-5 text-[#2C4C3B]" /><span className="font-semibold text-[#1C1C1C]">Box 1: Delay Exists</span></div>
                <p className="text-sm text-[#595959]">The student has a diagnosed speech or language disorder — such as an articulation problem, language delay, fluency issue (stuttering), or voice concern.</p>
              </div>
              <div className="bg-[#3B6A9B]/5 border border-[#3B6A9B]/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-5 h-5 text-[#3B6A9B]" /><span className="font-semibold text-[#1C1C1C]">Box 2: School Is Affected</span></div>
                <p className="text-sm text-[#595959]">The delay makes school harder for the student — data/evidence that shows the delay is affecting their grades, test performance, or ability to communicate with peers and teachers.</p>
              </div>
            </div>
            <p className="text-sm text-[#A3A3A3] mt-3 italic">If only one box is checked, the student does not qualify for special education under this category.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>What Types of Concerns Qualify?</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[{label:'Articulation',desc:'Making sounds incorrectly'},{label:'Language',desc:'Understanding or expressing words/ideas'},{label:'Fluency',desc:'Stuttering or disrupted speech flow'},{label:'Voice',desc:'Unusual voice quality or pitch'}].map((item) => (
                <div key={item.label} className="bg-[#F3F4F1] rounded-xl p-4 text-center"><p className="font-medium text-[#1C1C1C] text-sm">{item.label}</p><p className="text-xs text-[#595959] mt-1">{item.desc}</p></div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Who Makes the Decision?</h3>
            <div className="bg-[#F3F4F1] rounded-xl p-5">
              <p className="text-sm text-[#595959] mb-2"><strong className="text-[#1C1C1C]">Step 1:</strong> A certified Speech-Language Pathologist (SLP) conducts a full evaluation of the student.</p>
              <p className="text-sm text-[#595959] mb-2"><strong className="text-[#1C1C1C]">Step 2:</strong> The IEP team — which includes you, the SLP, parents, and an administrator — reviews the results together.</p>
              <p className="text-sm text-[#595959]"><strong className="text-[#1C1C1C]">Step 3:</strong> The team makes the eligibility decision as a group.</p>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Important: What Does NOT Count</h3>
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <p className="text-sm text-[#595959] mb-3">A student <strong className="text-[#1C1C1C]">cannot</strong> be found eligible based on any of the following alone:</p>
              <ul className="text-sm text-[#595959] space-y-2 ml-4 list-disc">
                <li><strong className="text-[#1C1C1C]">Lack of appropriate reading instruction</strong> — if the child just wasn't taught reading properly, that alone can't be the reason</li>
                <li><strong className="text-[#1C1C1C]">Lack of appropriate math instruction</strong> — same idea for math</li>
                <li><strong className="text-[#1C1C1C]">Limited English proficiency</strong> — a child who is still learning English as a second language cannot be found eligible just because of that language difference</li>
                <li><strong className="text-[#1C1C1C]">Dialect differences</strong> — speaking a regional or cultural dialect (like African American Vernacular English) is not a disorder and cannot be the basis for eligibility</li>
                <li><strong className="text-[#1C1C1C]">Cultural or environmental differences</strong> — a child's background, culture, or home environment alone cannot be the determining factor</li>
                <li><strong className="text-[#1C1C1C]">Economic disadvantage</strong> — poverty or lack of resources at home does not count as the cause</li>
                <li><strong className="text-[#1C1C1C]">No adverse educational effect</strong> — even if a child does have a speech/language difference, if it's NOT significantly affecting how they do at school, they still don't qualify</li>
              </ul>
            </div>
          </div>
          <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-xl p-5">
            <p className="text-sm text-[#595959] leading-relaxed"><strong className="text-[#1C1C1C]">In simple terms:</strong> The student has a speech or language difference that makes school harder for them. Your SLP will evaluate the student, and if both are true — the disorder exists AND school is affected — the student may qualify for additional support.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
